import { motion } from 'framer-motion';
import { Receipt, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { formatCurrency } from '../../utils';

interface EstimateSummarySectionProps {
  taskDecisions?: Record<string, { accepted?: boolean; declined?: boolean }>;
}

export function EstimateSummarySection({ taskDecisions = {} }: EstimateSummarySectionProps) {
  const proposal = useProposalData();

  // Only show for service proposals with tasks
  if (proposal.proposalType !== 'service' || !proposal.tasks || proposal.tasks.length === 0) {
    return null;
  }

  // Get current state of each task (merge proposal data with local decisions)
  const tasksWithStatus = proposal.tasks.map(task => ({
    ...task,
    accepted: taskDecisions[task.lineId]?.accepted ?? task.accepted,
    declined: taskDecisions[task.lineId]?.declined ?? task.declined,
  }));

  // Calculate totals
  const acceptedTasks = tasksWithStatus.filter(t => t.accepted);
  const declinedTasks = tasksWithStatus.filter(t => t.declined);
  const pendingTasks = tasksWithStatus.filter(t => !t.accepted && !t.declined);

  const acceptedTotal = acceptedTasks.reduce((sum, t) => sum + t.price, 0);
  const originalTotal = tasksWithStatus.reduce((sum, t) => sum + t.price, 0);

  const allTasksReviewed = pendingTasks.length === 0;

  return (
    <Section id="estimate-summary" dark className="bg-gradient-to-br from-hul-navy via-hul-navy-dark to-surface-darker">
      <SectionHeader
        title="ESTIMATE SUMMARY"
        subtitle="Your Selections"
        dark
      />

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column - Task Breakdown */}
        <div className="lg:col-span-2">
          <motion.div
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-heading text-white uppercase tracking-wider text-lg mb-6">
              Task Decisions
            </h3>

            {/* Task List */}
            <div className="space-y-3">
              {tasksWithStatus.map((task, index) => (
                <motion.div
                  key={task.lineId}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    task.accepted
                      ? 'bg-green-500/10 border border-green-500/30'
                      : task.declined
                      ? 'bg-red-500/10 border border-red-500/30'
                      : 'bg-white/5 border border-white/10'
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center gap-3">
                    {task.accepted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : task.declined ? (
                      <XCircle className="w-5 h-5 text-red-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-400" />
                    )}
                    <div>
                      <div className="text-white font-medium">{task.description}</div>
                      <div className="text-white/50 text-xs uppercase tracking-wider">
                        {task.taskCode}
                      </div>
                    </div>
                  </div>
                  <div className={`font-mono text-lg ${
                    task.accepted
                      ? 'text-green-400'
                      : task.declined
                      ? 'text-red-400 line-through'
                      : 'text-white'
                  }`}>
                    {formatCurrency(task.price)}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Status Bar */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex flex-wrap gap-4 justify-center">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 rounded-full">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm">
                    {acceptedTasks.length} Accepted
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 rounded-full">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm">
                    {pendingTasks.length} Pending
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-full">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm">
                    {declinedTasks.length} Declined
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Total & Submit */}
        <div>
          <motion.div
            className="bg-white rounded-2xl shadow-2xl overflow-hidden sticky top-24"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-hul-red to-hul-red-dark p-6">
              <div className="flex items-center gap-3 text-white mb-2">
                <Receipt className="w-6 h-6" />
                <span className="font-heading uppercase tracking-wider">
                  Your Total
                </span>
              </div>
            </div>

            {/* Pricing Details */}
            <div className="p-6">
              {/* Original Estimate */}
              <div className="flex justify-between items-center text-gray-500 mb-2">
                <span>Original Estimate</span>
                <span className="font-mono">{formatCurrency(originalTotal)}</span>
              </div>

              {/* Declined Amount */}
              {declinedTasks.length > 0 && (
                <div className="flex justify-between items-center text-red-500 mb-2">
                  <span>Declined Tasks</span>
                  <span className="font-mono">
                    -{formatCurrency(declinedTasks.reduce((sum, t) => sum + t.price, 0))}
                  </span>
                </div>
              )}

              {/* Divider */}
              <div className="my-4 border-t border-gray-200" />

              {/* Approved Total */}
              <div className="flex justify-between items-center mb-6">
                <span className="font-heading text-hul-navy uppercase tracking-wider text-lg">
                  Approved Total
                </span>
                <span className="font-mono text-3xl font-bold text-hul-red">
                  {formatCurrency(acceptedTotal)}
                </span>
              </div>

              {/* Pending Warning */}
              {!allTasksReviewed && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-amber-800 text-sm font-medium">
                        {pendingTasks.length} task{pendingTasks.length !== 1 ? 's' : ''} still pending
                      </p>
                      <p className="text-amber-600 text-xs mt-1">
                        Review all tasks above, then scroll to "Next Steps" to accept.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
